import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhoneAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV } from './nav';

interface Call { call_id: string; from_number: string; to_number: string; status: string; duration: number; cost: number; created_at: string; }

const CALL_STATUS_COLORS: Record<string, string> = {
    completed: 'success', answered: 'success', initiated: 'info', ringing: 'info',
    failed: 'danger', busy: 'warning', no_answer: 'secondary',
};

const CallCenter: React.FC = () => {
    const { api } = useInternalApi();
    const [calls, setCalls] = useState<Call[]>([]);
    const [byStatus, setByStatus] = useState<{ status: string; count: number }[]>([]);
    const [avgDuration, setAvgDuration] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Call Center - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('admin-dashboard/support/call-center/').then(res => {
            if (res.data?.status === 'success') {
                setCalls(res.data.data.calls || []);
                setByStatus(res.data.data.by_status || []);
                setAvgDuration(res.data.data.avg_duration_seconds || 0);
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faPhoneAlt} className="me-2 text-primary" />Call Center</h2>
            <p className="text-muted mb-4">Voice call log and outcomes over the last 30 days</p>

            <div className="alert alert-secondary small">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                This page reports call history and outcomes. Placing outbound calls is a live telecom action with real per-minute cost, so no dialer is wired in here.
            </div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Calls (30d)</div><h4>{calls.length}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Avg Duration</div><h4>{avgDuration}s</h4>
                            </div></div>
                        </div>
                    </div>

                    {byStatus.length > 0 && (
                        <div className="d-flex gap-2 mb-4 flex-wrap">
                            {byStatus.map(s => (
                                <span key={s.status} className={`badge bg-${CALL_STATUS_COLORS[s.status] || 'secondary'} p-2`}>{s.status}: {s.count}</span>
                            ))}
                        </div>
                    )}

                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white"><strong>Recent Calls</strong></div>
                        <div className="card-body p-0">
                            {calls.length === 0 ? (
                                <div className="text-center py-5 text-muted">No calls logged in the last 30 days.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Call ID</th><th>From</th><th>To</th><th>Status</th><th>Duration</th><th>Cost</th><th>When</th></tr></thead>
                                    <tbody>
                                        {calls.map(c => (
                                            <tr key={c.call_id}>
                                                <td className="small">{c.call_id}</td>
                                                <td>{c.from_number}</td>
                                                <td>{c.to_number}</td>
                                                <td><span className={`badge bg-${CALL_STATUS_COLORS[c.status] || 'secondary'}`}>{c.status}</span></td>
                                                <td>{c.duration}s</td>
                                                <td>{c.cost.toFixed(2)}</td>
                                                <td>{new Date(c.created_at).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default CallCenter;
